const EPSILON = 1e-6;

export interface IdentityViolation {
  balance: string;
  open: number;
  close: number;
  expectedClose: number;
  delta: number;
}

// Core identity checks. Verifies that each independent pool follows
// `close = open + inflationAccrual − depletion` and that the waiting-period
// counters clamp at zero against the consumed days.
//
// Derived totals (ltcPool = base + eob, cumulative payouts, cumulative
// ineligible cost) are no longer stored — they're computed on demand via
// `projectionTotals`, so there's nothing to drift and nothing to check here.
//
// Strategy-specific identities (CM II's indemnitySurplus, etc.) live in
// each strategy's own tests — `strategyState` is opaque to the engine.
export function checkProjectionIdentities(month: MonthlyEntry): IdentityViolation[] {
  const openState = month.openState;
  const close = month.state;
  const { deltas, facts } = month;
  const violations: IdentityViolation[] = [];

  const check = (balance: string, open: number, expectedClose: number, actualClose: number) => {
    const delta = actualClose - expectedClose;
    if (Math.abs(delta) > EPSILON) {
      violations.push({ balance, open, close: actualClose, expectedClose, delta });
    }
  };

  // Per-pool identities. Each LTC sub-pool follows `close = open +
  // inflationAccrual − depletion`. The EP retroactive lump's base portion
  // is rolled into `deltas.base.depletion` by the strategy, so it does
  // not appear separately here.
  check(
    'baseBenefitPool',
    openState.pools.baseBenefitPool,
    openState.pools.baseBenefitPool + deltas.base.inflationAccrual - deltas.base.depletion,
    close.pools.baseBenefitPool,
  );

  check(
    'extensionOfBenefitsPool',
    openState.pools.extensionOfBenefitsPool,
    openState.pools.extensionOfBenefitsPool + deltas.eob.inflationAccrual - deltas.eob.depletion,
    close.pools.extensionOfBenefitsPool,
  );

  check(
    'homeWaitingPeriodDays',
    openState.waitingPeriod.homeDays,
    Math.max(openState.waitingPeriod.homeDays - facts.waitingPeriodDaysConsumedHome, 0),
    close.waitingPeriod.homeDays,
  );

  check(
    'facilityWaitingPeriodDays',
    openState.waitingPeriod.facilityDays,
    Math.max(openState.waitingPeriod.facilityDays - facts.waitingPeriodDaysConsumedFacility, 0),
    close.waitingPeriod.facilityDays,
  );

  // deathBenefit has product-specific identities (CM II depletes by base
  // dollars + EP base lump; floored at the guaranteed minimum). Strategies
  // own that assertion.

  return violations;
}

export function assertProjectionIdentities(month: MonthlyEntry): void {
  const violations = checkProjectionIdentities(month);
  if (violations.length === 0) return;

  const summary = violations
    .map(
      v =>
        `${v.balance}: open=${v.open}, expectedClose=${v.expectedClose}, actualClose=${v.close}, delta=${v.delta}`,
    )
    .join('\n  ');
  throw new Error(
    `Projection identity violation at monthIndex=${month.context.monthIndex} (carePhase=${month.context.carePhase}):\n  ${summary}`,
  );
}
